import { from, merge, Observable, of } from 'rxjs';
import {
  ISetWebsitePartnersAction,
  ISetWebsiteWeddingDateAction,
  WeddingActionTypes,
} from '@bridebook/web/lib/weddings/action-types';
import type { Action, IApplicationState, IEpicDeps } from '@bridebook/web/lib/types';
import { ofType } from 'redux-observable';
import { catchError, mergeMap, withLatestFrom } from 'rxjs/operators';
import { appError } from '@bridebook/web/lib/app/actions';
import {
  getWebsiteConfig,
  getWebsiteDraftTimestamp,
  getWebsitePartners,
  getWebsiteSections,
  getWebsiteWeddingDate,
  getWeddingId,
} from '@bridebook/web/lib/weddings/selectors';
import { Weddings } from '@bridebook/models';
import {
  onFetchWebsiteDraftToggle,
  updateDraftTimestamp,
  updateWeddingField,
} from '@bridebook/web/lib/weddings/actions';
import { IWedding_Draft } from '@bridebook/models/source/models/Weddings.types';
import { Timestamp } from 'firebase/firestore';
import { PartialRecursive } from '@bridebook/models/source/abstract/_';

type TArgs = {
  state: IApplicationState;
  data: PartialRecursive<Omit<IWedding_Draft, 'meta'>>;
  ignoreAnalytics?: boolean;
};

const updateWeddingDraft = ({ state, data, ignoreAnalytics = false }: TArgs): Observable<any> => {
  const weddingId = getWeddingId(state);

  return from(Weddings._.getById(weddingId).get()).pipe(
    mergeMap((dbWeddingData) => {
      const dbDraft = dbWeddingData.draft;
      const dbTimestamp = dbDraft?.meta?.updatedAt.toMillis() || 0;

      const uiTimestamp = getWebsiteDraftTimestamp(state);

      // add 10000 ms for the lag
      if (dbTimestamp > uiTimestamp + 10000) {
        return of(onFetchWebsiteDraftToggle({ show: true }));
      }
      const newDraft = {
        meta: {
          updatedAt: Timestamp.fromDate(new Date()),
        },
        ...data,
      };

      return merge(
        of(updateDraftTimestamp({ updatedAt: newDraft.meta.updatedAt.toMillis() })),
        of(updateWeddingField('draft', { draft: newDraft }, undefined, ignoreAnalytics)),
      );
    }),
  );
};
export const updateWeddingDraftWebsiteSection = (
  action$: Observable<Action>,
  { state$ }: IEpicDeps,
) =>
  action$.pipe(
    ofType(
      WeddingActionTypes.WEBSITE_UPDATE_SECTION,
      WeddingActionTypes.WEBSITE_REORDER_SECTIONS,
      WeddingActionTypes.WEBSITE_UPDATE_TEXT_FIELD,
      WeddingActionTypes.WEBSITE_UPDATE_PHOTOS,
      WeddingActionTypes.WEBSITE_ADD_QUESTION_ANSWER,
      WeddingActionTypes.WEBSITE_REMOVE_QUESTION_ANSWER,
      WeddingActionTypes.WEBSITE_UPDATE_QUESTION_ANSWER,
      WeddingActionTypes.WEBSITE_UPDATE_GIFT_LIST,
      WeddingActionTypes.WEBSITE_UPDATE_HOTELS,
      WeddingActionTypes.WEBSITE_SET_SECTION_VISIBILITY,
      WeddingActionTypes.WEBSITE_SET_QUESTION_ANSWER_SECTION_DATA,
    ),
    withLatestFrom(state$),
    mergeMap(([_, state]) =>
      updateWeddingDraft({
        state,
        data: { website: { sections: getWebsiteSections(state) } },
        ignoreAnalytics: true,
      }),
    ),
    catchError((error: Error) =>
      of(appError({ error, feature: 'Updating wedding draft - update section' })),
    ),
  );

export const updateWeddingDraftWebsiteConfig = (
  action$: Observable<Action>,
  { state$ }: IEpicDeps,
) =>
  action$.pipe(
    ofType(
      WeddingActionTypes.WEBSITE_SET_THEME,
      WeddingActionTypes.WEBSITE_SET_THEME_FONT,
      WeddingActionTypes.WEBSITE_SET_THEME_PALETTE,
      WeddingActionTypes.WEBSITE_SET_RSVP_VISIBLE,
      WeddingActionTypes.WEBSITE_SET_LAYOUT_VERSION,
      WeddingActionTypes.WEBSITE_SET_CLASSIC_DESIGN,
      WeddingActionTypes.WEBSITE_SET_MOTIF_SELECTION,
    ),
    withLatestFrom(state$),
    mergeMap(([_, state]) =>
      updateWeddingDraft({
        state,
        data: { website: { config: getWebsiteConfig(state) } },
        ignoreAnalytics: true,
      }),
    ),
    catchError((error: Error) =>
      of(appError({ error, feature: 'Updating wedding draft - config' })),
    ),
  );

export const updateWeddingDraftPartners = (
  action$: Observable<ISetWebsitePartnersAction>,
  { state$ }: IEpicDeps,
) =>
  action$.pipe(
    ofType(WeddingActionTypes.WEBSITE_SET_PARTNERS),
    withLatestFrom(state$),
    mergeMap(([_, state]) =>
      updateWeddingDraft({
        state,
        data: { partners: getWebsitePartners(state) },
        ignoreAnalytics: true,
      }),
    ),
    catchError((error: Error) =>
      of(appError({ error, feature: 'Updating wedding draft - partners' })),
    ),
  );

export const updateWeddingDraftDate = (
  action$: Observable<ISetWebsiteWeddingDateAction>,
  { state$ }: IEpicDeps,
) =>
  action$.pipe(
    ofType(WeddingActionTypes.WEBSITE_SET_WEDDING_DATE),
    withLatestFrom(state$),
    mergeMap(([_, state]) =>
      updateWeddingDraft({
        state,
        data: { date: getWebsiteWeddingDate(state) },
        ignoreAnalytics: true,
      }),
    ),
    catchError((error: Error) =>
      of(appError({ error, feature: 'Updating wedding draft - wedding date' })),
    ),
  );
